import React, { useCallback } from 'react';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import useUser from '@vl-core/hooks/useUser';
import { useDispatch } from 'react-redux';
import * as actions from '@actions';
import * as actionTypes from '@actionTypes';
import { useRouter } from 'next/router';
import { Modal, Result } from 'antd';
import useUserNextAppointments from '@hooks/useUserNextAppointments';
import { useTranslation } from 'react-i18next';

const useDnaAppointment = () => {
  const user = useUser();
  const [, invalidateAppointments] = useUserNextAppointments(10);
  const { appointment } = useSessionAppointment();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const router = useRouter();
  const { t } = useTranslation();

  const returnToAppointmentsPage = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['clinicianAppointment']
    });
    router.push('/clinician/appointments').then();
  }, [queryClient, router]);

  const { mutateAsync: dnaAppointment, isPending: isDNAAppointmentLoading } = useMutation({
    mutationFn: async () => {
      const reasonResponse = await dispatch(
        actions.rescheduleCancel({
          user_guid: user.user_guid,
          appt_id: appointment.appointment_id.toString(),
          reason: 'DNA'
        })
      );
      if (reasonResponse.type === actionTypes.RESCHEDULE_CANCEL_SUCCESS) return { status: 200 };
      throw new Error('Failed to mark appointment as DNA');
    },
    onSuccess: () => {
      Modal.success({
        centered: true,
        icon: <></>,
        width: '30rem',
        title: (
          <Result
            status="info"
            title={t('Appointment Cancelled')}
            subTitle="This appointment has now been cancelled and the member will be contacted by our admin team. A rescheduled appointment may be created when checks have been created"
          />
        ),
        modalRender: (originalNode) => <div data-testid="appointment-cancelled-modal">{originalNode}</div>,
        onOk: () => {
          invalidateAppointments();
          returnToAppointmentsPage();
        },
        okText: 'Go to Appointments',
        okButtonProps: { style: { display: 'block', margin: '0 auto 2rem auto' } }
      });
    },
    onError: () => {
      Modal.error({
        title: 'Unable to cancel appointment',
        content: 'There was an error cancelling this appointment.',
        okText: <span data-testid="ok">{t('OK')}</span>,
        centered: true
      });
    }
  });

  const onDnaAppointment = useCallback(() => {
    if (appointment.appointment_completed) {
      Modal.error({
        title: 'Unable to mark as DNA',
        content: 'This appointment has already been completed and cannot be marked as DNA.',
        okText: 'Go to Appointments',
        closable: true,
        centered: true,
        onOk: () => {
          returnToAppointmentsPage();
        }
      });
      return;
    }
    Modal.confirm({
      title: 'DNA Appointment Confirmation',
      content: 'Are you sure you want to mark this appointment as DNA?',
      okText: 'Confirm',
      closable: true,
      okButtonProps: { loading: isDNAAppointmentLoading },
      onOk: async () => {
        await dnaAppointment().catch(() => {});
      }
    });
  }, [appointment, dnaAppointment, isDNAAppointmentLoading, returnToAppointmentsPage]);

  return {
    onDnaAppointment
  };
};

export { useDnaAppointment };
